import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';

// Standard WhatsApp Flow categories accepted by Meta when creating a flow.
const FLOW_CATEGORIES = [
  'SIGN_UP',
  'SIGN_IN',
  'APPOINTMENT_BOOKING',
  'LEAD_GENERATION',
  'CONTACT_US',
  'CUSTOMER_SUPPORT',
  'SURVEY',
  'OTHER',
];

const SaveFlowDialog = ({ open, onClose, onSave, flowSummary, existingFlow, flowType = 'STATIC' }) => {
  // A dynamic flow asks for live data while the customer fills it in. Where that comes from is
  // set in the flow's Connections (FlowConnectionsDrawer), not here: FlowGate forwards to the
  // owner's server through the flow's own route, so there is no endpoint or key to type in.
  const isDynamicFlow = flowType === 'DYNAMIC';
  const [flowName, setFlowName] = useState('');
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState(['OTHER']);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // currentFlow can be truthy without an id (e.g. template-loaded or
  // imported-but-not-yet-saved flows). Only treat it as "editing" when it
  // has a persisted id, otherwise this is a create.
  const isExistingSavedFlow = !!existingFlow?.id;

  // Pre-fill form when opening
  React.useEffect(() => {
    if (open && existingFlow) {
      // Useful even for unsaved templates: gives the user a sensible default name.
      setFlowName(existingFlow.name || '');
      setDescription(existingFlow.description || '');
      setCategories(
        Array.isArray(existingFlow.categories) && existingFlow.categories.length > 0
          ? existingFlow.categories
          : ['OTHER'],
      );
    } else if (open) {
      setFlowName('');
      setDescription('');
      setCategories(['OTHER']);
    }
    if (open) {
      setError('');
    }
  }, [open, existingFlow]);

  const handleSave = async () => {
    if (!flowName.trim()) {
      setError('Flow name is required');
      return;
    }

    // onSave (FlowBuilder.handleSaveFlow) regenerates flowJson via generateFlowJson
    //, the canonical async path that handles _builderMeta, conditions, routing, and
    // image processing. We deliberately pass only the user-entered fields here.
    try {
      setIsSaving(true);
      setError('');
      // FlowBuilder.handleSaveFlow signals a pre-save validation failure by
      // returning null (it surfaces the message via a snackbar). Don't close
      // the dialog in that case, the user would lose their typed name and
      // description and have to start over.
      const result = await onSave({
        name: flowName.trim(),
        description: description.trim(),
        categories,
      });
      if (result !== null && result !== undefined) {
        handleClose();
      }
    } catch (err) {
      setError(err.message || 'Failed to save flow');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isSaving) return; // Prevent closing while saving
    setFlowName('');
    setDescription('');
    setCategories(['OTHER']);
    setError('');
    onClose();
  };

  const screenCount = flowSummary?.screenCount || 0;
  const componentCount = flowSummary?.componentCount || 0;
  const version = flowSummary?.version || 'Unknown';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth aria-labelledby="save-flow-dialog-title">
      <DialogTitle id="save-flow-dialog-title">
        {isExistingSavedFlow ? 'Update Flow' : 'Save WhatsApp Flow'}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {existingFlow
              ? 'Update your flow with the current WhatsApp Flow changes.'
              : 'Save your WhatsApp Flow to use and manage later.'
            }
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          label="Flow Name"
          fullWidth
          variant="outlined"
          value={flowName}
          onChange={(e) => {
            setFlowName(e.target.value);
            setError('');
          }}
          placeholder="e.g., Customer Feedback Flow"
          sx={{ mb: 2 }}
          required
          error={!!error}
        />

        <TextField
          label="Description (Optional)"
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of what this flow is for..."
          sx={{ mb: 2 }}
        />

        {/* Categories apply to every flow, shown inline rather than tucked
            behind an "Advanced" section. */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="flow-categories-label">Categories</InputLabel>
          <Select
            labelId="flow-categories-label"
            label="Categories"
            multiple
            value={categories}
            onChange={(e) => {
              const value = e.target.value;
              setCategories(typeof value === 'string' ? value.split(',') : value);
            }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {FLOW_CATEGORIES.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isDynamicFlow && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="save-dialog-live-data-note">
            This flow looks up data while a customer fills it in. After saving, open its
            Connections to say where that data comes from.
          </Alert>
        )}

        <Box sx={{
          p: 2,
          bgcolor: 'background.default',
          borderRadius: 1,
          border: 1,
          borderColor: 'divider'
        }}>
          <Typography variant="subtitle2" gutterBottom>
            Flow Summary:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • {screenCount} screen{screenCount !== 1 ? 's' : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • {componentCount} component{componentCount !== 1 ? 's' : ''} total
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Version: {version}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} color="inherit" disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={!flowName.trim() || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isSaving ? 'Saving...' : (isExistingSavedFlow ? 'Update Flow' : 'Save Flow')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveFlowDialog;
